/**
 * @author Mr_FabiozZz[mr.fabiozzz@gmail.com]
 */
import React, { useMemo } from "react";
import sass from "./check.module.sass";
import cn from "classnames";
import { ReactComponent as LikeIcon } from "images/like.svg";

export interface INewCheckBox
	extends React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
	type: "checkbox" | "radio";
	label?: string;
	scale?: "lg" | "md" | "sm" | "xs";
}

/**
 * @description компонент кнопки (радио/чекбокс) намеренно делал без подписи, так как подписи не везде имеют стандартный вид
 *
 * @example пример использования
  const Foo:React.FC = ()=>{
     const [someField,setSomeField] = useState({
          fieldOne:true,
          fieldTwo:false,
          fieldThree:true
     })
     const changeItem = (event: React.ChangeEvent<HTMLInputElement>)=>{
          const {name,checked} = e.target
          setSomeField(prevState=>({...prevState,[name]:!checked}))
     }
     return (
     <>
         <label>
              <NewCheckBox name={'fieldOne'} checked={someField.fieldOne} onChange={changeItem}/>
              Значение 1
         </label>
         <label>
              <NewCheckBox name={'fieldTwo'} checked={someField.fieldTwo} onChange={changeItem}/>
              Значение 2
         </label>
         <label>
              <NewCheckBox name={'fieldThree'} checked={someField.fieldThree} onChange={changeItem}/>
              Значение 3
         </label>

     </>
  )}

 * @param {INewCheckBox} props
 * @param {('checkbox'|'radio')} props.type - тип кнопки радио или чекбокс
 * @param {boolean} [props.disabled] - стандартный для инпута аттрибут
 * @param {('lg'|'md'|'sm')} [props.scale] - размер кнопки радио/чекбокс (16x11/15x15;12x12/14x10;10x10/11x8)
 * @param {any} props.rest - остальное стандартные элементы для кнопки
 * @constructor
 */
const NewCheckBox: React.FC<INewCheckBox> = ({ type, disabled, scale = "sm", ...rest }) => {
	const sizes = useMemo(() => {
		const like = {
			...(scale === "sm"
				? { width: 11, height: 8 }
				: scale === "md"
				? { width: 14, height: 10 }
				: { width: 16, height: 11 }),
		};
		const dot = {
			...(scale === "sm"
				? { width: 10, height: 10 }
				: scale === "md"
				? { width: 12, height: 12 }
				: { width: 15, height: 15 }),
		};
		return { like, dot };
	}, [scale]);

	return (
		<label className={sass.wrapper}>
			<input className={sass.input} type={type} hidden={true} disabled={disabled} {...rest} />
			<span
				tabIndex={disabled ? undefined : 0}
				className={cn(sass[type], sass[`size_${scale}`], sass[`${type}__disabled-${disabled || false}`], {
					[sass[`${type}-active`]]: rest.checked,
				})}>
				{type === "checkbox" && <LikeIcon {...sizes.like} />}
			</span>
		</label>
	);
};

export default NewCheckBox;
